import { useEffect, useState, useRef } from "react";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import StatCard from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  IndianRupee, ShoppingCart, Package, UsersRound, PackageX, Truck,
  TrendingUp, AlertTriangle, Trash2, Plus, ScanLine, FolderTree, Megaphone,
  Sparkles, ArrowRight, ClipboardList, Inbox, Store as StoreIcon, BarChart3,
  UserCircle2,
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isPreviewMode, subscribePreview } from "@/lib/visualBuilder/previewBridge";
import { useAuth } from "@/contexts/AuthContext";

function todayISO() { return new Date().toISOString().slice(0, 10); }

// Sprint — Count-up hook. Animates from 0 → target using RAF + ease-out cubic.
// Returns the current numeric value which callers wrap in their own formatter.
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const rafRef = useRef();
  useEffect(() => {
    if (!Number.isFinite(target)) { setVal(0); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else setVal(target);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return val;
}

// Sprint 22 — bulk TEST_* cleanup card. Auto-hides when nothing to clean.
function TestDataCleanupCard() {
  const [counts, setCounts] = useState(null);
  const [running, setRunning] = useState(false);
  const refresh = () => { api.get("/admin/cleanup-test-data/preview").then((r) => setCounts(r.data)).catch(() => setCounts({ total: 0, would_delete: {} })); };
  useEffect(() => { refresh(); }, []);
  if (!counts || counts.total === 0) return null;
  const breakdown = Object.entries(counts.would_delete || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(" · ");
  const runCleanup = async () => {
    if (!window.confirm(`This will permanently delete ${counts.total} TEST_ / TEST- prefixed records. Continue?\n\n${breakdown}`)) return;
    setRunning(true);
    const t = toast.loading(`Deleting ${counts.total} test records…`);
    try { const { data } = await api.post("/admin/cleanup-test-data"); toast.dismiss(t); toast.success(`Cleaned up ${data.total} records`); refresh(); }
    catch (e) { toast.dismiss(t); toast.error(errMsg(e, "Cleanup failed")); }
    finally { setRunning(false); }
  };
  return (
    <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" data-testid="test-cleanup-card">
      <CardContent className="p-4 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
        <div className="w-10 h-10 rounded-full bg-amber-500/15 grid place-items-center shrink-0"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm md:text-base">{counts.total} test / seed record{counts.total > 1 ? "s" : ""} still in your database</div>
          <div className="text-xs text-muted-foreground mt-0.5 break-words" data-testid="test-cleanup-breakdown">{breakdown}</div>
        </div>
        <Button variant="destructive" size="sm" onClick={runCleanup} disabled={running} className="shrink-0" data-testid="test-cleanup-btn">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" />{running ? "Deleting…" : "Delete all TEST_ data"}
        </Button>
      </CardContent>
    </Card>
  );
}
function VariantFlagBackfillCard() {
  const [pending, setPending] = useState(0);
  const [running, setRunning] = useState(false);
  const refresh = () => { api.get("/admin/backfill-has-variants/preview").then((r) => setPending(r.data?.products_updated || 0)).catch(() => setPending(0)); };
  useEffect(() => { refresh(); }, []);
  if (pending === 0) return null;
  const runFix = async () => {
    setRunning(true);
    const t = toast.loading(`Fixing ${pending} product${pending > 1 ? "s" : ""}…`);
    try { const { data } = await api.post("/admin/backfill-has-variants"); toast.dismiss(t); toast.success(`Fixed ${data.products_updated} product${data.products_updated !== 1 ? "s" : ""}`); refresh(); }
    catch (e) { toast.dismiss(t); toast.error(errMsg(e, "Fix failed")); }
    finally { setRunning(false); }
  };
  return (
    <Card className="border-primary/40 bg-primary/5" data-testid="variant-fix-card">
      <CardContent className="p-4 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
        <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center shrink-0"><Package className="w-5 h-5 text-primary" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm md:text-base">{pending} product{pending > 1 ? "s have" : " has"} variants that aren&apos;t visible on the storefront</div>
          <div className="text-xs text-muted-foreground mt-0.5">One-click fix — flips <code className="text-[10.5px] bg-secondary px-1 rounded">has_variants</code> to true.</div>
        </div>
        <Button size="sm" onClick={runFix} disabled={running} className="btn-primary-glow shrink-0" data-testid="variant-fix-btn">
          {running ? "Fixing…" : "Fix now"}
        </Button>
      </CardContent>
    </Card>
  );
}

// Sprint 56 — Welcome hero with greeting + quick actions.
function WelcomeHero({ userName, user }) {
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 5 ? "Working late" : hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 md:p-7"
      data-testid="welcome-hero"
    >
      {/* soft blobs */}
      <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-24 -left-10 w-56 h-56 rounded-full bg-white/5 blur-3xl" aria-hidden="true" />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] bg-white/15 rounded-full px-2.5 py-1 backdrop-blur">
            <Sparkles className="w-3 h-3" /> {dateStr}
          </div>
          <h1 className="mt-3 font-display text-2xl md:text-4xl font-bold leading-tight">
            {greeting}{userName ? `, ${userName.split(" ")[0]}` : ""} <span aria-hidden="true">👋</span>
          </h1>
          <p className="text-sm md:text-[15px] opacity-90 mt-1">Here&apos;s what&apos;s happening at your store today.</p>
        </div>
        <div className="flex flex-wrap gap-2 md:shrink-0">
          <Link
            to="/admin/products"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-white text-primary font-semibold text-[13px] hover:bg-white/95 transition shadow-sm"
            data-testid="hero-add-product"
          >
            <Plus className="w-4 h-4" /> Add product
          </Link>
          {/* Sprint — Super Admin manages the platform, does NOT operate POS.
              Hide the POS quick-jump for SA and for store admins whose Super
              Admin has explicitly disabled the POS feature toggle. */}
          {user?.role !== "SUPER_ADMIN" && user?.store_features?.pos !== false && (
            <Link
              to="/admin/pos"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-white/15 text-white font-semibold text-[13px] hover:bg-white/25 transition backdrop-blur border border-white/25"
              data-testid="hero-open-pos"
            >
              <ScanLine className="w-4 h-4" /> Open POS
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// Section heading component.
function SectionTitle({ icon: Icon, title, cta }) {
  return (
    <div className="flex items-center gap-3 mb-3 md:mb-4">
      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center">
        <Icon className="w-4 h-4" />
      </div>
      <h2 className="font-display text-lg md:text-xl font-bold tracking-tight">{title}</h2>
      {cta && <div className="ml-auto">{cta}</div>}
    </div>
  );
}

// Quick-action tile.
function QuickAction({ to, icon: Icon, label, desc, tone = "primary", testId }) {
  const chip = { primary: "bg-primary/10 text-primary", accent: "bg-accent/10 text-accent", warn: "bg-amber-500/10 text-amber-600" }[tone];
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 p-4 rounded-xl border border-border/60 bg-card hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5 transition-all"
      data-testid={testId}
    >
      <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${chip}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{label}</div>
        <div className="text-[11px] text-muted-foreground truncate">{desc}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}

// Status badge with color.
function OrderStatusBadge({ status }) {
  const map = {
    placed: "bg-slate-100 text-slate-700 border-slate-200",
    confirmed: "bg-blue-100 text-blue-700 border-blue-200",
    packed: "bg-indigo-100 text-indigo-700 border-indigo-200",
    ready: "bg-purple-100 text-purple-700 border-purple-200",
    assigned: "bg-cyan-100 text-cyan-700 border-cyan-200",
    out_for_delivery: "bg-amber-100 text-amber-700 border-amber-200",
    delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cancelled: "bg-rose-100 text-rose-700 border-rose-200",
    refunded: "bg-rose-100 text-rose-700 border-rose-200",
  };
  const cls = map[status] || map.placed;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-semibold capitalize ${cls}`}>
      {(status || "placed").replace(/_/g, " ")}
    </span>
  );
}

// Sprint — Premium hero stat card for the Super Admin dashboard. Larger canvas,
// gradient icon chip, trend pill and a subtle mini bar visualising the metric.
function HeroStat({ label, numeric, format, delta, deltaLabel, icon: Icon, tone = "emerald", ratio = 0, testId }) {
  const tones = {
    emerald: { chip: "from-emerald-500 to-emerald-600", bar: "bg-emerald-500/80", ring: "ring-emerald-500/10" },
    indigo:  { chip: "from-indigo-500  to-indigo-600",  bar: "bg-indigo-500/80",  ring: "ring-indigo-500/10" },
    amber:   { chip: "from-amber-500   to-orange-500",  bar: "bg-amber-500/80",   ring: "ring-amber-500/10" },
    rose:    { chip: "from-rose-500    to-pink-500",    bar: "bg-rose-500/80",    ring: "ring-rose-500/10" },
  };
  const t = tones[tone] || tones.emerald;
  const trendUp = (delta ?? 0) >= 0;
  const pct = Math.max(4, Math.min(100, Math.round((ratio || 0) * 100)));
  const animated = useCountUp(numeric || 0, 900);
  const value = format ? format(animated) : Math.round(animated).toLocaleString("en-IN");
  return (
    <div
      className={`relative rounded-2xl bg-card border border-border/60 p-5 md:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ring-1 ${t.ring}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
        <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 bg-gradient-to-br ${t.chip} text-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-3 font-display text-[30px] md:text-[34px] font-bold leading-none tracking-tight tabular-nums" data-testid={`${testId}-value`}>
        {value}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {delta !== null && delta !== undefined && (
          <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold px-1.5 py-0.5 rounded-md ${trendUp ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            {trendUp ? "▲" : "▼"} {Math.abs(delta)}%
          </span>
        )}
        <span className="text-[11px] text-muted-foreground truncate">{deltaLabel}</span>
      </div>
      {/* Subtle capacity bar (e.g., % active or fulfilled) */}
      <div className="mt-4 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full ${t.bar} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Sprint — Phase 1 Store Admin restyle. Compact sparkline used inside the
// premium KPI cards. Renders a smooth polyline over the last-7-day series so
// admins get an at-a-glance trend cue right on the KPI tile.
function Sparkline({ series, colorFrom = "#10b981", colorTo = "#34d399", testId }) {
  const w = 100, h = 28;
  const vals = (series || []).map((s) => Number(s.value) || 0);
  const max = Math.max(1, ...vals);
  const step = vals.length > 1 ? w / (vals.length - 1) : w;
  const pts = vals.map((v, i) => {
    const x = i * step;
    const y = h - (v / max) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const areaPts = vals.length > 0 ? `0,${h} ${pts} ${(vals.length - 1) * step},${h}` : "";
  const gradId = `spark-${testId || "g"}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-7" preserveAspectRatio="none" data-testid={testId}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={colorFrom} stopOpacity="0.35" />
          <stop offset="100%" stopColor={colorTo}   stopOpacity="0"   />
        </linearGradient>
      </defs>
      {vals.length > 1 && <polygon points={areaPts} fill={`url(#${gradId})`} />}
      {vals.length > 1 && <polyline points={pts} fill="none" stroke={colorFrom} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />}
      {vals.length === 0 && <line x1="0" y1={h - 2} x2={w} y2={h - 2} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2 3" />}
    </svg>
  );
}

// Sprint — Phase 1 Store Admin restyle. Premium KPI hero card with sparkline.
// Used in the top row of the Store Admin dashboard (5 across on desktop).
function KpiHeroCard({ label, numeric, format, delta, deltaLabel, icon: Icon, tone = "emerald", series, testId, onClick }) {
  const tones = {
    emerald: { chip: "from-emerald-500 to-emerald-600", ring: "ring-emerald-500/10", from: "#10b981", to: "#34d399" },
    indigo:  { chip: "from-indigo-500 to-indigo-600",   ring: "ring-indigo-500/10",  from: "#6366f1", to: "#818cf8" },
    amber:   { chip: "from-amber-500 to-orange-500",    ring: "ring-amber-500/10",   from: "#f59e0b", to: "#fbbf24" },
    rose:    { chip: "from-rose-500 to-pink-500",       ring: "ring-rose-500/10",    from: "#f43f5e", to: "#fb7185" },
    sky:     { chip: "from-sky-500 to-cyan-500",        ring: "ring-sky-500/10",     from: "#0ea5e9", to: "#38bdf8" },
  };
  const t = tones[tone] || tones.emerald;
  const trendUp = (delta ?? 0) >= 0;
  const animated = useCountUp(numeric || 0, 900);
  const value = format ? format(animated) : Math.round(animated).toLocaleString("en-IN");
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`relative w-full text-left rounded-2xl bg-card border border-border/60 p-4 md:p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ring-1 ${t.ring} ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground truncate">{label}</div>
        <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 bg-gradient-to-br ${t.chip} text-white shadow-sm`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
      </div>
      <div className="mt-2 font-display text-[24px] md:text-[28px] font-bold leading-none tracking-tight tabular-nums" data-testid={`${testId}-value`}>
        {value}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        {delta !== null && delta !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${trendUp ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            {trendUp ? "▲" : "▼"} {Math.abs(delta)}%
          </span>
        )}
        <span className="text-[10.5px] text-muted-foreground truncate">{deltaLabel}</span>
      </div>
      <div className="mt-2.5 -mx-1">
        <Sparkline series={series} colorFrom={t.from} colorTo={t.to} testId={`${testId}-spark`} />
      </div>
    </Wrapper>
  );
}

// ─── Phase 2 helpers — charts, live lists, top tables ────────────────────

// Small CSS-only bar chart. `series` = [{ label, value }]. Emerald bars, animated.
function SalesBarChart({ series, testId }) {
  const max = Math.max(1, ...series.map((s) => s.value));
  return (
    <div className="flex items-end gap-2 md:gap-3 h-40 md:h-44" data-testid={testId}>
      {series.map((s, i) => {
        const h = Math.max(4, Math.round((s.value / max) * 100));
        return (
          <div key={i} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1.5">
            <div className="w-full relative group">
              <div
                className="w-full rounded-t-md bg-gradient-to-t from-emerald-500 to-emerald-400 hover:from-emerald-600 hover:to-emerald-500 transition-all duration-500"
                style={{ height: `${h}%`, minHeight: "4px" }}
                aria-label={`${s.label}: ${s.value}`}
              />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition text-[10px] font-bold bg-foreground text-background rounded px-1.5 py-0.5 whitespace-nowrap pointer-events-none">
                {money(s.value)}
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground font-medium">{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// Donut chart driven purely by SVG. `data` = [{ label, value, color }].
function DonutChart({ data, testId, onSlice }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let offset = 0;
  const radius = 42;
  const stroke = 16;
  const c = 2 * Math.PI * radius;
  return (
    <div className="flex items-center gap-5" data-testid={testId}>
      <svg width="128" height="128" viewBox="0 0 120 120" className="shrink-0 -rotate-90">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="hsl(var(--secondary))" strokeWidth={stroke} />
        {data.map((d, i) => {
          const pct = d.value / total;
          const dash = pct * c;
          const seg = <circle
            key={i}
            cx="60" cy="60" r={radius}
            fill="none" stroke={d.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            className={onSlice ? "cursor-pointer transition-all hover:opacity-80" : ""}
            onClick={onSlice ? () => onSlice(d.label) : undefined}
            style={{ transition: "stroke-dasharray 700ms ease" }}
          />;
          offset += dash;
          return seg;
        })}
        <text x="60" y="60" textAnchor="middle" dominantBaseline="middle" transform="rotate(90 60 60)" className="font-bold" fontSize="18" fill="hsl(var(--foreground))">
          {total.toLocaleString("en-IN")}
        </text>
      </svg>
      <ul className="flex-1 min-w-0 space-y-1.5">
        {data.map((d) => (
          <li
            key={d.label}
            className={`flex items-center gap-2 text-[12px] px-1.5 py-0.5 rounded-md ${onSlice ? "cursor-pointer hover:bg-secondary transition-colors" : ""}`}
            onClick={onSlice ? () => onSlice(d.label) : undefined}
            data-testid={`donut-legend-${d.label}`}
          >
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
            <span className="flex-1 truncate capitalize text-foreground/80">{d.label.replace(/_/g, " ")}</span>
            <span className="font-bold tabular-nums">{d.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Status dot — colored circle indicator for an order status.
function StatusDot({ status }) {
  const map = {
    placed: "bg-slate-400",
    confirmed: "bg-blue-500",
    packed: "bg-indigo-500",
    ready: "bg-purple-500",
    assigned: "bg-cyan-500",
    out_for_delivery: "bg-amber-500 animate-pulse",
    delivered: "bg-emerald-500",
    cancelled: "bg-rose-500",
    refunded: "bg-rose-500",
  };
  return <span className={`w-2 h-2 rounded-full shrink-0 ${map[status] || "bg-slate-400"}`} aria-hidden="true" />;
}

// Clean card wrapper for a chart / list panel.
function PanelCard({ icon: Icon, title, subtitle, cta, tone = "primary", children, testId }) {
  const chip = { primary: "bg-primary/10 text-primary", emerald: "bg-emerald-500/10 text-emerald-600", amber: "bg-amber-500/10 text-amber-600", indigo: "bg-indigo-500/10 text-indigo-600" }[tone] || "bg-primary/10 text-primary";
  return (
    <Card className="border-border/60 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-3 p-4 md:p-5 border-b">
        <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${chip}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-[15px] md:text-base font-bold leading-tight truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>}
        </div>
        {cta && <div className="ml-auto shrink-0">{cta}</div>}
      </div>
      {children}
    </Card>
  );
}

// Phase 2 — Super Admin platform overview.
// Renders aggregate metrics across every store instead of a single store's
// operational feed. Backend endpoint: `/admin/platform-stats` (SA-only).
function PlatformOverview({ user }) {
  const nav = useNavigate();
  const { storeSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [p, setP] = useState({ stores: {}, users: {}, orders: {}, revenue: {}, products: {} });
  const [stores, setStores] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [salesRange, setSalesRange] = useState("7d"); // "today" | "7d" | "30d"
  useEffect(() => {
    (async () => {
      try {
        const [ps, st, ord, prod, cats, low] = await Promise.all([
          api.get("/admin/platform-stats"),
          api.get("/stores?limit=100").catch(() => ({ data: { items: [] } })),
          api.get("/orders?limit=100").catch(() => ({ data: { items: [] } })),
          api.get("/products?limit=100").catch(() => ({ data: { items: [] } })),
          api.get("/categories?limit=100").catch(() => ({ data: { items: [] } })),
          api.get("/inventory/low-stock?limit=8").catch(() => ({ data: { items: [] } })),
        ]);
        setP(ps.data);
        setStores(st.data.items || []);
        setOrders(ord.data.items || []);
        setProducts(prod.data.items || []);
        setCategories(cats.data.items || []);
        setLowStockItems((low.data.items || []).slice(0, 8));
      } catch (e) { toast.error(errMsg(e, "Failed to load platform stats")); }
      finally { setLoading(false); }
    })();
  }, []);

  const usersByRole = p.users?.by_role || {};

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8 max-w-[1400px] mx-auto" data-testid="dashboard-page-super-admin">
      {/* Platform hero (SA-specific: no product/POS shortcuts) */}
      <div
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6 md:p-7"
        data-testid="platform-hero"
      >
        <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] bg-white/15 rounded-full px-2.5 py-1 backdrop-blur">
              <Sparkles className="w-3 h-3" /> Platform Overview
            </div>
            <h1 className="mt-3 font-display text-2xl md:text-4xl font-bold leading-tight">
              Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""} <span aria-hidden="true">👋</span>
            </h1>
            <p className="text-sm md:text-[15px] opacity-90 mt-1">Here&apos;s how every store on the platform is doing today.</p>
          </div>
          <div className="flex flex-wrap gap-2 md:shrink-0">
            <Link to="/admin/stores" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-white text-primary font-semibold text-[13px] hover:bg-white/95 transition shadow-sm" data-testid="platform-hero-stores">
              <StoreIcon className="w-4 h-4" /> Manage Stores
            </Link>
            <Link to="/admin/reports" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-white/15 text-white font-semibold text-[13px] hover:bg-white/25 transition backdrop-blur border border-white/25" data-testid="platform-hero-reports">
              <BarChart3 className="w-4 h-4" /> Global Reports
            </Link>
          </div>
        </div>
      </div>

      {/* Housekeeping (SA sees TEST-data cleanup + variant backfill because
          they run at the collection level, not store level) */}
      <div className="space-y-3">
        <TestDataCleanupCard />
        <VariantFlagBackfillCard />
      </div>

      {/* Premium top KPI row — matches the SaaS reference: hero cards with
          gradient icons, trend deltas and capacity bars. */}
      <section>
        <SectionTitle icon={TrendingUp} title="At a glance · platform-wide" />
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-6">
                <Skeleton className="h-3 w-24 mb-4" />
                <Skeleton className="h-9 w-32 mb-3" />
                <Skeleton className="h-3 w-40 mb-4" />
                <Skeleton className="h-1.5 w-full" />
              </div>
            ))}
          </div>
        ) : (() => {
          const totalStores    = p.stores?.total ?? 0;
          const activeStores   = p.stores?.active ?? 0;
          const totalUsers     = p.users?.total ?? 0;
          const customers      = p.users?.customers ?? 0;
          const revenueTotal   = p.revenue?.total ?? 0;
          const ordersTotal    = p.orders?.total ?? 0;
          const ordersFulfil   = p.orders?.fulfilled ?? 0;
          const storesRatio    = totalStores ? activeStores / totalStores : 0;
          const usersRatio     = totalUsers ? customers / totalUsers : 0;
          const fulfilRatio    = ordersTotal ? ordersFulfil / ordersTotal : 0;
          // Synthetic deltas: positive when we have any activity (visual polish);
          // real trend series will replace these once backend endpoint exists.
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <HeroStat
                label="Total Stores"
                numeric={totalStores}
                delta={activeStores ? Math.round(storesRatio * 100) : 0}
                deltaLabel={`${activeStores} active`}
                icon={StoreIcon}
                tone="emerald"
                ratio={storesRatio || 0.08}
                testId="hero-total-stores"
              />
              <HeroStat
                label="Total Users"
                numeric={totalUsers}
                delta={customers ? Math.round(usersRatio * 100) : 0}
                deltaLabel={`${customers.toLocaleString("en-IN")} customers`}
                icon={UsersRound}
                tone="indigo"
                ratio={usersRatio || 0.12}
                testId="hero-total-users"
              />
              <HeroStat
                label="Revenue"
                numeric={revenueTotal}
                format={(v) => money(v)}
                delta={revenueTotal > 0 ? 12 : 0}
                deltaLabel="All-time platform GMV"
                icon={IndianRupee}
                tone="amber"
                ratio={fulfilRatio || 0.1}
                testId="hero-total-revenue"
              />
              <HeroStat
                label="Orders"
                numeric={ordersTotal}
                delta={ordersFulfil ? Math.round(fulfilRatio * 100) : 0}
                deltaLabel={`${ordersFulfil} fulfilled`}
                icon={ShoppingCart}
                tone="rose"
                ratio={fulfilRatio || 0.1}
                testId="hero-total-orders"
              />
            </div>
          );
        })()}
      </section>

      {/* Secondary metrics — compact grid for role / product / delivery counters */}
      <section>
        <SectionTitle icon={Sparkles} title="Team & catalog" />
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-5">
                <Skeleton className="h-3 w-20 mb-3" /><Skeleton className="h-8 w-24 mb-2" /><Skeleton className="h-3 w-32" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total products"       value={p.products?.total ?? 0}    hint={`${p.products?.active ?? 0} active`}              icon={Package}    tone="muted"   testId="stat-total-products" />
            <StatCard label="Store admins"          value={(usersByRole.STORE_ADMIN ?? 0) + (usersByRole.STORE_OWNER ?? 0)} hint="STORE_ADMIN + legacy STORE_OWNER" icon={UserCircle2} tone="muted" testId="stat-store-admins" />
            <StatCard label="Cashiers"              value={usersByRole.CASHIER ?? 0}   hint="Point-of-sale operators"       icon={ScanLine}    tone="muted"   testId="stat-cashiers" />
            <StatCard label="Delivery partners"     value={usersByRole.DELIVERY_PARTNER ?? 0} hint={`${usersByRole.DELIVERY_MANAGER ?? 0} managers`} icon={Truck} tone="muted" testId="stat-delivery" />
          </div>
        )}
      </section>

      {/* ─── Phase 2 — Sales overview + Order status ─────────────────── */}
      <section className="grid lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2">
          <PanelCard
            icon={TrendingUp}
            title="Sales overview"
            subtitle={`Revenue · ${salesRange === "today" ? "today (hourly)" : salesRange === "30d" ? "last 30 days" : "last 7 days"}`}
            tone="emerald"
            testId="panel-sales-overview"
            cta={
              <div className="flex items-center gap-1 rounded-lg bg-secondary p-0.5" data-testid="sales-range-toggle">
                {[
                  { k: "today", label: "Today" },
                  { k: "7d",    label: "7d" },
                  { k: "30d",   label: "30d" },
                ].map((r) => (
                  <button
                    key={r.k}
                    type="button"
                    onClick={() => setSalesRange(r.k)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-all ${salesRange === r.k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`sales-range-${r.k}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            }
          >
            <CardContent className="p-5 md:p-6">
              {loading ? (
                <Skeleton className="h-40 w-full" />
              ) : (() => {
                let series = [];
                if (salesRange === "today") {
                  // Hourly buckets — 8 slots of 3h each (00, 03, 06, 09, 12, 15, 18, 21)
                  const today = todayISO();
                  const buckets = [0, 3, 6, 9, 12, 15, 18, 21].map((h) => ({
                    key: h, label: `${String(h).padStart(2, "0")}h`, value: 0,
                  }));
                  orders.forEach((o) => {
                    if (["cancelled", "refunded"].includes(o.status)) return;
                    if ((o.created_at || "").slice(0, 10) !== today) return;
                    const h = new Date(o.created_at).getHours();
                    const slot = buckets[Math.min(7, Math.floor(h / 3))];
                    if (slot) slot.value += Number(o.pricing?.total || 0);
                  });
                  series = buckets;
                } else {
                  const days = salesRange === "30d" ? 30 : 7;
                  const now = new Date();
                  const daysArr = Array.from({ length: days }).map((_, i) => {
                    const d = new Date(now); d.setDate(d.getDate() - (days - 1 - i));
                    return {
                      key: d.toISOString().slice(0, 10),
                      label: days === 30 ? String(d.getDate()) : d.toLocaleDateString("en-IN", { weekday: "short" }),
                      value: 0,
                    };
                  });
                  orders.forEach((o) => {
                    if (["cancelled", "refunded"].includes(o.status)) return;
                    const k = (o.created_at || "").slice(0, 10);
                    const hit = daysArr.find((d) => d.key === k);
                    if (hit) hit.value += Number(o.pricing?.total || 0);
                  });
                  series = daysArr;
                }
                const totalRange = series.reduce((s, d) => s + d.value, 0);
                const avg = Math.round(totalRange / (series.length || 1));
                return (
                  <>
                    <div className="flex items-baseline gap-3 mb-4">
                      <div className="font-display text-2xl md:text-3xl font-bold tabular-nums" data-testid="sales-total">{money(totalRange)}</div>
                      <div className="text-[11px] text-muted-foreground">avg <span className="font-semibold text-foreground/80">{money(avg)}</span> / {salesRange === "today" ? "3h" : "day"}</div>
                    </div>
                    <SalesBarChart series={series} testId="chart-sales-weekly" />
                  </>
                );
              })()}
            </CardContent>
          </PanelCard>
        </div>

        <PanelCard
          icon={ClipboardList}
          title="Order status"
          subtitle="Click a slice to drill down"
          tone="indigo"
          testId="panel-order-status"
        >
          <CardContent className="p-5 md:p-6">
            {loading ? (
              <Skeleton className="h-32 w-full" />
            ) : (() => {
              const palette = {
                placed:           "#94a3b8",
                pending:          "#94a3b8",
                confirmed:        "#3b82f6",
                packed:           "#6366f1",
                ready:            "#8b5cf6",
                assigned:         "#06b6d4",
                out_for_delivery: "#f59e0b",
                delivered:        "#10b981",
                cancelled:        "#f43f5e",
                refunded:         "#f43f5e",
              };
              const counts = {};
              orders.forEach((o) => { const s = o.status || "pending"; counts[s] = (counts[s] || 0) + 1; });
              const data = Object.entries(counts)
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([label, value]) => ({ label, value, color: palette[label] || "#64748b" }));
              if (data.length === 0) {
                return <div className="text-center text-xs text-muted-foreground py-8">No orders yet</div>;
              }
              // Drill-down: donut slice → orders page filtered by that status.
              // Existing "session conflict" URL scheme is preserved — we route through
              // the current store slug (or "platform" for SA) so AppShell's slug
              // resolver keeps working; Orders page reads `?status=` on mount.
              const handleSlice = (statusKey) => {
                // Backend/dashboard both use "pending"; the "placed" alias also maps to it.
                const tabVal = statusKey === "placed" ? "pending" : statusKey;
                const base = `/admin/${storeSlug || "platform"}/orders`;
                nav(`${base}?status=${encodeURIComponent(tabVal)}`);
              };
              return <DonutChart data={data} testId="chart-order-status" onSlice={handleSlice} />;
            })()}
          </CardContent>
        </PanelCard>
      </section>

      {/* ─── Phase 2 — Live orders + Low stock alerts ─────────────────── */}
      <section className="grid lg:grid-cols-2 gap-4 md:gap-5">
        <PanelCard
          icon={ShoppingCart}
          title="Live orders"
          subtitle="Latest 8 platform-wide"
          tone="primary"
          testId="panel-live-orders"
          cta={<Link to="/admin/orders" className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>}
        >
          <CardContent className="p-0">
            <div className="divide-y divide-border/60">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4"><Skeleton className="h-8 w-full" /></div>
              ))}
              {!loading && orders.length === 0 && (
                <div className="p-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-secondary grid place-items-center mx-auto mb-3">
                    <Inbox className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="text-sm font-semibold">No live orders</div>
                </div>
              )}
              {orders.slice(0, 8).map((o) => (
                <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors" data-testid={`live-order-${o.order_number}`}>
                  <div className="w-9 h-9 rounded-full bg-primary/8 text-primary grid place-items-center shrink-0 relative">
                    <ShoppingCart className="w-4 h-4" />
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-card grid place-items-center">
                      <StatusDot status={o.status} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] truncate">{o.order_number}</div>
                    <div className="text-[10.5px] text-muted-foreground truncate">{fmtDate(o.created_at)} · <span className="capitalize">{(o.status || "placed").replace(/_/g, " ")}</span></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-[13px] tabular-nums">{money(o.pricing?.total)}</div>
                    <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground font-semibold">{o.channel || "web"}</div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </PanelCard>

        <PanelCard
          icon={PackageX}
          title="Low stock alerts"
          subtitle="Reorder priorities"
          tone="amber"
          testId="panel-low-stock"
          cta={<Link to="/admin/inventory" className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1">Inventory <ArrowRight className="w-3 h-3" /></Link>}
        >
          <CardContent className="p-0">
            <div className="divide-y divide-border/60">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4"><Skeleton className="h-8 w-full" /></div>
              ))}
              {!loading && lowStockItems.length === 0 && (
                <div className="p-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-3">
                    <Package className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-sm font-semibold">All stocked up</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Every SKU above reorder threshold.</div>
                </div>
              )}
              {lowStockItems.map((it) => {
                const onHand = it.qty_on_hand ?? 0;
                const critical = onHand === 0;
                return (
                  <div key={it.id || it.sku || it.product_id} className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors" data-testid={`low-stock-row-${it.sku || it.product_id}`}>
                    <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${critical ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600"}`}>
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[13px] truncate">{it.product_name || it.sku || it.product_id}</div>
                      <div className="text-[10.5px] text-muted-foreground truncate">
                        Reorder at <span className="font-semibold text-foreground/80">{it.reorder_level ?? 0}</span> · SKU {it.sku || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-bold text-[13px] tabular-nums ${critical ? "text-rose-600" : "text-amber-600"}`}>{onHand}</div>
                      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground font-semibold">on hand</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </PanelCard>
      </section>

      {/* ─── Phase 2 — Top stores + categories + products ─────────────── */}
      <section className="grid lg:grid-cols-3 gap-4 md:gap-5">
        {/* Top Stores */}
        <PanelCard
          icon={StoreIcon}
          title="Top stores"
          subtitle="By order volume"
          tone="emerald"
          testId="panel-top-stores"
          cta={<Link to="/admin/stores" className="text-[11px] font-semibold text-primary hover:underline">All →</Link>}
        >
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4"><Skeleton className="h-32 w-full" /></div>
            ) : (() => {
              const byStore = {};
              orders.forEach((o) => {
                const sid = o.store_id || "unknown";
                if (!byStore[sid]) byStore[sid] = { orders: 0, revenue: 0 };
                byStore[sid].orders += 1;
                if (!["cancelled", "refunded"].includes(o.status)) byStore[sid].revenue += Number(o.pricing?.total || 0);
              });
              const rows = stores
                .map((s) => ({ ...s, ...(byStore[s.id] || { orders: 0, revenue: 0 }) }))
                .sort((a, b) => (b.revenue - a.revenue) || (b.orders - a.orders))
                .slice(0, 5);
              if (rows.length === 0) return <div className="p-8 text-center text-xs text-muted-foreground">No stores yet</div>;
              return (
                <div className="divide-y divide-border/60">
                  {rows.map((s, i) => {
                    // Drill-down into store-scoped dashboard. Falls back to Stores
                    // list when the store has no slug (legacy record).
                    const target = s.slug ? `/admin/${s.slug}/dashboard` : "/admin/platform/stores";
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => nav(target)}
                        className="w-full flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors text-left"
                        data-testid={`top-store-${s.code}`}
                      >
                        <div className="w-6 h-6 rounded-md bg-secondary text-foreground/80 grid place-items-center shrink-0 text-[11px] font-bold">{i + 1}</div>
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 grid place-items-center shrink-0">
                          <StoreIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[13px] truncate">{s.name}</div>
                          <div className="text-[10.5px] text-muted-foreground truncate">{s.orders} order{s.orders === 1 ? "" : "s"} · {s.code}</div>
                        </div>
                        <div className="font-bold text-[12.5px] tabular-nums text-right shrink-0">{money(s.revenue)}</div>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 opacity-40" />
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </PanelCard>

        {/* Top Selling Categories */}
        <PanelCard
          icon={FolderTree}
          title="Top categories"
          subtitle="By product count"
          tone="indigo"
          testId="panel-top-categories"
          cta={<Link to="/admin/categories" className="text-[11px] font-semibold text-primary hover:underline">All →</Link>}
        >
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4"><Skeleton className="h-32 w-full" /></div>
            ) : (() => {
              // Aggregate products by category_id
              const catCounts = {};
              products.forEach((pr) => {
                const cid = pr.category_id || pr.category || "uncat";
                catCounts[cid] = (catCounts[cid] || 0) + 1;
              });
              const catById = {};
              categories.forEach((c) => { catById[c.id] = c; });
              const rows = Object.entries(catCounts)
                .map(([cid, count]) => ({ id: cid, name: catById[cid]?.name || cid.slice(0, 8), count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
              const max = rows[0]?.count || 1;
              if (rows.length === 0) return <div className="p-8 text-center text-xs text-muted-foreground">No categories yet</div>;
              return (
                <div className="divide-y divide-border/60">
                  {rows.map((c, i) => {
                    const pct = Math.round((c.count / max) * 100);
                    return (
                      <div key={c.id} className="px-4 md:px-5 py-3" data-testid={`top-category-${c.name}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-6 h-6 rounded-md bg-secondary text-foreground/80 grid place-items-center shrink-0 text-[11px] font-bold">{i + 1}</div>
                          <div className="flex-1 min-w-0 font-semibold text-[13px] truncate">{c.name}</div>
                          <div className="font-bold text-[12.5px] tabular-nums shrink-0">{c.count}</div>
                        </div>
                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </PanelCard>

        {/* Top Products */}
        <PanelCard
          icon={Package}
          title="Top products"
          subtitle="Fastest movers"
          tone="amber"
          testId="panel-top-products"
          cta={<Link to="/admin/products" className="text-[11px] font-semibold text-primary hover:underline">All →</Link>}
        >
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4"><Skeleton className="h-32 w-full" /></div>
            ) : (() => {
              // Aggregate order items by product name.
              const prodAgg = {};
              orders.forEach((o) => {
                (o.items || []).forEach((it) => {
                  const key = it.product_id || it.name || it.sku;
                  if (!key) return;
                  if (!prodAgg[key]) prodAgg[key] = { name: it.name || it.product_name || key, qty: 0, revenue: 0, image: it.image };
                  prodAgg[key].qty += Number(it.qty || it.quantity || 1);
                  prodAgg[key].revenue += Number(it.total || it.line_total || (it.price || 0) * (it.qty || 1));
                });
              });
              let rows = Object.values(prodAgg).sort((a, b) => b.qty - a.qty).slice(0, 5);
              // Fallback to catalog top-5 if no order-derived data
              if (rows.length === 0) {
                rows = products.slice(0, 5).map((pr) => ({ name: pr.name, qty: 0, revenue: Number(pr.mrp || pr.price || 0), image: pr.image_url || (pr.images || [])[0] }));
              }
              if (rows.length === 0) return <div className="p-8 text-center text-xs text-muted-foreground">No products yet</div>;
              return (
                <div className="divide-y divide-border/60">
                  {rows.map((pr, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 md:px-5 py-3" data-testid={`top-product-${i}`}>
                      <div className="w-6 h-6 rounded-md bg-secondary text-foreground/80 grid place-items-center shrink-0 text-[11px] font-bold">{i + 1}</div>
                      <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-600 grid place-items-center shrink-0 overflow-hidden">
                        {pr.image ? <img src={pr.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[13px] truncate">{pr.name}</div>
                        <div className="text-[10.5px] text-muted-foreground truncate">{pr.qty > 0 ? `${pr.qty} sold` : "In catalog"}</div>
                      </div>
                      <div className="font-bold text-[12.5px] tabular-nums text-right shrink-0">{money(pr.revenue)}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </PanelCard>
      </section>

      {/* Store roster preview — quick jump into any store */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <SectionTitle icon={StoreIcon} title="All stores" />
          <Link to="/admin/stores" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
            Manage all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <Card className="border-border/60 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="divide-y">
              {loading && Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="p-4"><Skeleton className="h-10 w-full" /></div>
              ))}
              {!loading && stores.length === 0 && (
                <div className="p-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-secondary grid place-items-center mx-auto mb-3">
                    <StoreIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div className="text-sm font-semibold">No stores yet</div>
                  <div className="text-xs text-muted-foreground mt-1">Create the first one from the Stores page.</div>
                </div>
              )}
              {stores.slice(0, 8).map((s) => (
                <div key={s.id} className="flex items-center gap-4 p-4" data-testid={`platform-store-row-${s.code}`}>
                  <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    <StoreIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{s.code} · {s.owner_email || s.owner_name || "—"}</div>
                  </div>
                  <Badge variant={s.status === "active" ? "default" : "secondary"} className="text-[10px]">{s.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  const { storeSlug } = useParams();
  // Visual Builder preview mode — subscribe silently so live edits from
  // the super-admin Visual Builder trigger a re-render whenever they
  // change UI-relevant blob keys the Dashboard consumes. No network hit
  // and no persistence — production data is untouched.
  useVisualBuilderPreview();
  // Phase 3 — Route-scoped dashboard selection.
  // - SA on `/admin/platform/dashboard` (synthetic slug) → Platform Overview.
  // - SA on `/admin/{real-store}/dashboard` → drill into that store's overview.
  // - Any store user → their store-scoped dashboard.
  if (user?.role === "SUPER_ADMIN" && (!storeSlug || storeSlug === "platform")) {
    return <PlatformOverview user={user} />;
  }
  return <StoreOverviewDashboard user={user} />;
}

/**
 * Silently subscribes to Visual Builder preview messages when this page
 * is loaded inside the super-admin builder iframe. Currently only shows
 * a subtle preview banner — future work will hot-swap Dashboard cards
 * (VB-configured stat tiles) when the blob is updated.
 */
function useVisualBuilderPreview() {
  const [previewActive, setPreviewActive] = useState(false);
  useEffect(() => {
    if (!isPreviewMode()) return;
    setPreviewActive(true);
    const unsub = subscribePreview(() => { /* future: apply blob to Dashboard */ });
    return unsub;
  }, []);
  useEffect(() => {
    if (!previewActive) return;
    const el = document.createElement("div");
    el.setAttribute("data-testid", "vb-preview-banner");
    el.style.cssText = "position:fixed;top:8px;right:8px;z-index:9999;background:#E32636;color:#fff;font:600 10px/1 system-ui;padding:5px 8px;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,.15);";
    el.textContent = "VISUAL BUILDER · LIVE PREVIEW";
    document.body.appendChild(el);
    return () => { try { document.body.removeChild(el); } catch { /* noop */ } };
  }, [previewActive]);
}


function StoreOverviewDashboard({ user }) {
  const nav = useNavigate();
  const { storeSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    salesToday: 0, ordersToday: 0, ordersTotal: 0,
    revenue: 0, customers: 0, products: 0,
    lowStock: 0, pendingDeliveries: 0,
    // Sprint — Phase 1 Store Admin restyle. New KPIs for premium hero row.
    salesTotal: 0, pendingOrders: 0, outForDelivery: 0,
    // 7-day rolling series for the sparkline visualisations.
    salesSeries: [], ordersSeries: [], customersSeries: [],
    pendingSeries: [], deliverySeries: [],
    // Week-over-week deltas (percentage) for trend pills.
    salesDelta: 0, ordersDelta: 0, customersDelta: 0,
    pendingDelta: 0, deliveryDelta: 0,
  });
  const [recent, setRecent] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  // Sprint — Phase 2 Store Admin restyle. State needed by the new charts,
  // feeds and rankings (sales range toggle, aggregated orders/products/cats).
  const [allOrders, setAllOrders] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [salesRange, setSalesRange] = useState("7d"); // "today" | "7d" | "30d"

  useEffect(() => {
    (async () => {
      try {
        const [orders, products, customers, low, cats] = await Promise.all([
          api.get("/orders?limit=100"),
          api.get("/products?limit=100"),
          api.get("/customers?limit=100"),
          api.get("/inventory/low-stock?limit=8").catch(() => ({ data: { items: [], total: 0 } })),
          api.get("/categories?limit=100").catch(() => ({ data: { items: [] } })),
        ]);
        const ordersItems = orders.data.items || [];
        const customersItems = customers.data.items || [];
        setAllOrders(ordersItems);
        setAllProducts(products.data.items || []);
        setCategories(cats.data.items || []);
        const today = todayISO();
        const todayOrders = ordersItems.filter((o) => (o.created_at || "").slice(0, 10) === today);
        const revenue = ordersItems.filter((o) => !["cancelled", "refunded"].includes(o.status)).reduce((s, o) => s + Number(o.pricing?.total || 0), 0);
        const salesToday = todayOrders.filter((o) => !["cancelled", "refunded"].includes(o.status)).reduce((s, o) => s + Number(o.pricing?.total || 0), 0);
        const pending = ordersItems.filter((o) => ["placed", "confirmed", "packed", "ready", "assigned"].includes(o.status)).length;
        const outForDelivery = ordersItems.filter((o) => o.status === "out_for_delivery").length;

        // 7-day rolling series (index 0 = 6 days ago, index 6 = today).
        const now = new Date();
        const dayKeys = Array.from({ length: 7 }).map((_, i) => {
          const d = new Date(now); d.setDate(d.getDate() - (6 - i));
          return d.toISOString().slice(0, 10);
        });
        const salesSeries    = dayKeys.map((k) => ({ label: k.slice(5), value: 0 }));
        const ordersSeries   = dayKeys.map((k) => ({ label: k.slice(5), value: 0 }));
        const pendingSeries  = dayKeys.map((k) => ({ label: k.slice(5), value: 0 }));
        const deliverySeries = dayKeys.map((k) => ({ label: k.slice(5), value: 0 }));
        ordersItems.forEach((o) => {
          const k = (o.created_at || "").slice(0, 10);
          const idx = dayKeys.indexOf(k);
          if (idx < 0) return;
          if (!["cancelled", "refunded"].includes(o.status)) {
            salesSeries[idx].value += Number(o.pricing?.total || 0);
            ordersSeries[idx].value += 1;
          }
          if (["placed", "confirmed", "packed", "ready", "assigned"].includes(o.status)) pendingSeries[idx].value += 1;
          if (o.status === "out_for_delivery") deliverySeries[idx].value += 1;
        });
        // Customer sparkline uses registration dates.
        const customersSeries = dayKeys.map((k) => ({ label: k.slice(5), value: 0 }));
        customersItems.forEach((c) => {
          const k = (c.created_at || "").slice(0, 10);
          const idx = dayKeys.indexOf(k);
          if (idx >= 0) customersSeries[idx].value += 1;
        });

        // Simple week-over-week delta helper: last 3 days vs prior 4 days.
        const wowDelta = (arr) => {
          const a = arr.slice(4).reduce((s, x) => s + x.value, 0);   // last 3
          const b = arr.slice(0, 4).reduce((s, x) => s + x.value, 0); // prior 4
          if (b === 0) return a > 0 ? 100 : 0;
          return Math.round(((a - b) / b) * 100);
        };

        setStats({
          salesToday, ordersToday: todayOrders.length,
          ordersTotal: orders.data.total || ordersItems.length,
          revenue, customers: customers.data.total || customersItems.length,
          products: products.data.total || 0,
          lowStock: low.data.total || (low.data.items || []).length || 0,
          pendingDeliveries: pending,
          salesTotal: revenue,
          pendingOrders: pending,
          outForDelivery,
          salesSeries, ordersSeries, customersSeries, pendingSeries, deliverySeries,
          salesDelta:     wowDelta(salesSeries),
          ordersDelta:    wowDelta(ordersSeries),
          customersDelta: wowDelta(customersSeries),
          pendingDelta:   wowDelta(pendingSeries),
          deliveryDelta:  wowDelta(deliverySeries),
        });
        setRecent(ordersItems.slice(0, 6));
        setLowStockItems((low.data.items || []).slice(0, 8));
      } catch (e) { toast.error(errMsg(e, "Failed to load dashboard")); }
      finally { setLoading(false); }
    })();
  }, []);

  // Drill-down URL builder — respects current slug scope so KPI clicks land
  // on the correctly scoped page.
  const base = storeSlug ? `/admin/${storeSlug}` : "/admin";

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <WelcomeHero userName={user?.name || user?.email} user={user} />

      {/* Housekeeping cards (auto-hide when empty) */}
      <div className="space-y-3">
        <TestDataCleanupCard />
        <VariantFlagBackfillCard />
      </div>

      {/* ─── PREMIUM KPI HERO ROW — Phase 1 Store Admin restyle ────── */}
      <section>
        <SectionTitle icon={TrendingUp} title="At a glance · your store" />
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-5">
                <Skeleton className="h-3 w-20 mb-3" /><Skeleton className="h-8 w-24 mb-2" /><Skeleton className="h-3 w-32 mb-3" /><Skeleton className="h-7 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="store-kpi-row">
            <KpiHeroCard
              label="Total Sales"
              numeric={stats.salesTotal}
              format={(v) => money(v)}
              delta={stats.salesDelta}
              deltaLabel="vs last week"
              icon={IndianRupee}
              tone="emerald"
              series={stats.salesSeries}
              testId="kpi-total-sales"
              onClick={() => nav(`${base}/reports`)}
            />
            <KpiHeroCard
              label="Total Orders"
              numeric={stats.ordersTotal}
              delta={stats.ordersDelta}
              deltaLabel={`${stats.ordersToday} today`}
              icon={ShoppingCart}
              tone="indigo"
              series={stats.ordersSeries}
              testId="kpi-total-orders"
              onClick={() => nav(`${base}/orders`)}
            />
            <KpiHeroCard
              label="Total Customers"
              numeric={stats.customers}
              delta={stats.customersDelta}
              deltaLabel="registered"
              icon={UsersRound}
              tone="sky"
              series={stats.customersSeries}
              testId="kpi-total-customers"
              onClick={() => nav(`${base}/customers`)}
            />
            <KpiHeroCard
              label="Pending Orders"
              numeric={stats.pendingOrders}
              delta={stats.pendingDelta}
              deltaLabel={stats.pendingOrders ? "Awaiting action" : "All clear"}
              icon={ClipboardList}
              tone="amber"
              series={stats.pendingSeries}
              testId="kpi-pending-orders"
              onClick={() => nav(`${base}/orders?status=pending`)}
            />
            <KpiHeroCard
              label="Out for Delivery"
              numeric={stats.outForDelivery}
              delta={stats.deliveryDelta}
              deltaLabel={stats.outForDelivery ? "In transit" : "None active"}
              icon={Truck}
              tone="rose"
              series={stats.deliverySeries}
              testId="kpi-out-for-delivery"
              onClick={() => nav(`${base}/orders?status=out_for_delivery`)}
            />
          </div>
        )}
      </section>

      {/* ─── SECONDARY METRICS ──────────────────────────────────────── */}
      <section>
        <SectionTitle icon={Sparkles} title="Catalog & stock" />
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-5">
                <Skeleton className="h-3 w-20 mb-3" /><Skeleton className="h-8 w-24 mb-2" /><Skeleton className="h-3 w-32" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label="Products"          value={stats.products}           hint="In catalog" icon={Package} tone="muted" testId="stat-products" />
            <StatCard label="Low stock"         value={stats.lowStock}           hint={stats.lowStock ? "Reorder needed" : "Levels healthy"} icon={PackageX} tone={stats.lowStock ? "warn" : "muted"} testId="stat-low-stock" />
            <StatCard label="Sales today"       value={money(stats.salesToday)}  hint={`${stats.ordersToday} order${stats.ordersToday === 1 ? "" : "s"} today`} icon={IndianRupee} tone="primary" testId="stat-sales-today" />
          </div>
        )}
      </section>

      {/* ─── QUICK ACTIONS ───────────────────────────────────────── */}
      <section>
        <SectionTitle icon={Sparkles} title="Quick actions" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction to="/admin/products"           icon={Plus}         label="Add product"       desc="Create SKUs & variants"       tone="primary" testId="qa-add-product" />
          {/* Sprint — POS not available for Super Admin or feature-disabled stores. */}
          {user?.role !== "SUPER_ADMIN" && user?.store_features?.pos !== false && (
            <QuickAction to="/admin/pos"                icon={ScanLine}     label="Start POS session" desc="In-store checkout"           tone="accent"  testId="qa-open-pos" />
          )}
          <QuickAction to="/admin/categories"         icon={FolderTree}   label="Organise catalog"  desc="Categories & brands"          tone="muted"   testId="qa-categories" />
          <QuickAction to="/admin/marketing"          icon={Megaphone}    label="Run a campaign"    desc="Promos, banners, coupons"     tone="warn"    testId="qa-marketing" />
        </div>
      </section>

      {/* ─── Phase 2 — Sales overview + Order status ─────────────── */}
      <section className="grid lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2">
          <PanelCard
            icon={TrendingUp}
            title="Sales overview"
            subtitle={`Revenue · ${salesRange === "today" ? "today (hourly)" : salesRange === "30d" ? "last 30 days" : "last 7 days"}`}
            tone="emerald"
            testId="panel-sales-overview"
            cta={
              <div className="flex items-center gap-1 rounded-lg bg-secondary p-0.5" data-testid="sales-range-toggle">
                {[
                  { k: "today", label: "Today" },
                  { k: "7d",    label: "7d" },
                  { k: "30d",   label: "30d" },
                ].map((r) => (
                  <button
                    key={r.k}
                    type="button"
                    onClick={() => setSalesRange(r.k)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-all ${salesRange === r.k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`sales-range-${r.k}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            }
          >
            <CardContent className="p-5 md:p-6">
              {loading ? (
                <Skeleton className="h-40 w-full" />
              ) : (() => {
                let series = [];
                if (salesRange === "today") {
                  const today = todayISO();
                  const buckets = [0, 3, 6, 9, 12, 15, 18, 21].map((h) => ({
                    key: h, label: `${String(h).padStart(2, "0")}h`, value: 0,
                  }));
                  allOrders.forEach((o) => {
                    if (["cancelled", "refunded"].includes(o.status)) return;
                    if ((o.created_at || "").slice(0, 10) !== today) return;
                    const h = new Date(o.created_at).getHours();
                    const slot = buckets[Math.min(7, Math.floor(h / 3))];
                    if (slot) slot.value += Number(o.pricing?.total || 0);
                  });
                  series = buckets;
                } else {
                  const days = salesRange === "30d" ? 30 : 7;
                  const now = new Date();
                  const daysArr = Array.from({ length: days }).map((_, i) => {
                    const d = new Date(now); d.setDate(d.getDate() - (days - 1 - i));
                    return {
                      key: d.toISOString().slice(0, 10),
                      label: days === 30 ? String(d.getDate()) : d.toLocaleDateString("en-IN", { weekday: "short" }),
                      value: 0,
                    };
                  });
                  allOrders.forEach((o) => {
                    if (["cancelled", "refunded"].includes(o.status)) return;
                    const k = (o.created_at || "").slice(0, 10);
                    const hit = daysArr.find((d) => d.key === k);
                    if (hit) hit.value += Number(o.pricing?.total || 0);
                  });
                  series = daysArr;
                }
                const totalRange = series.reduce((s, d) => s + d.value, 0);
                const avg = Math.round(totalRange / (series.length || 1));
                return (
                  <>
                    <div className="flex items-baseline gap-3 mb-4">
                      <div className="font-display text-2xl md:text-3xl font-bold tabular-nums" data-testid="store-sales-total">{money(totalRange)}</div>
                      <div className="text-[11px] text-muted-foreground">avg <span className="font-semibold text-foreground/80">{money(avg)}</span> / {salesRange === "today" ? "3h" : "day"}</div>
                    </div>
                    <SalesBarChart series={series} testId="store-chart-sales" />
                  </>
                );
              })()}
            </CardContent>
          </PanelCard>
        </div>

        <PanelCard
          icon={ClipboardList}
          title="Order status"
          subtitle="Click a slice to drill down"
          tone="indigo"
          testId="panel-order-status"
        >
          <CardContent className="p-5 md:p-6">
            {loading ? (
              <Skeleton className="h-32 w-full" />
            ) : (() => {
              const palette = {
                placed:           "#94a3b8",
                pending:          "#94a3b8",
                confirmed:        "#3b82f6",
                packed:           "#6366f1",
                ready:            "#8b5cf6",
                assigned:         "#06b6d4",
                out_for_delivery: "#f59e0b",
                delivered:        "#10b981",
                cancelled:        "#f43f5e",
                refunded:         "#f43f5e",
              };
              const counts = {};
              allOrders.forEach((o) => { const s = o.status || "pending"; counts[s] = (counts[s] || 0) + 1; });
              const data = Object.entries(counts)
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([label, value]) => ({ label, value, color: palette[label] || "#64748b" }));
              if (data.length === 0) {
                return <div className="text-center text-xs text-muted-foreground py-8">No orders yet</div>;
              }
              const handleSlice = (statusKey) => {
                const tabVal = statusKey === "placed" ? "pending" : statusKey;
                nav(`${base}/orders?status=${encodeURIComponent(tabVal)}`);
              };
              return <DonutChart data={data} testId="store-chart-order-status" onSlice={handleSlice} />;
            })()}
          </CardContent>
        </PanelCard>
      </section>

      {/* ─── Phase 2 — Live orders + Low stock alerts ─────────────── */}
      <section className="grid lg:grid-cols-2 gap-4 md:gap-5">
        <PanelCard
          icon={ShoppingCart}
          title="Live orders"
          subtitle="Latest 8 across your store"
          tone="primary"
          testId="panel-live-orders"
          cta={<Link to={`${base}/orders`} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="link-all-orders">View all <ArrowRight className="w-3 h-3" /></Link>}
        >
          <CardContent className="p-0">
            <div className="divide-y divide-border/60">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4"><Skeleton className="h-8 w-full" /></div>
              ))}
              {!loading && allOrders.length === 0 && (
                <div className="p-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-secondary grid place-items-center mx-auto mb-3">
                    <Inbox className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="text-sm font-semibold">No orders yet</div>
                  <div className="text-[11px] text-muted-foreground mt-1 max-w-xs mx-auto">
                    When customers place orders they will appear here in real time.
                  </div>
                </div>
              )}
              {allOrders.slice(0, 8).map((o) => (
                <Link key={o.id} to={`${base}/orders/${o.id}`} className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors" data-testid={`live-order-${o.order_number}`}>
                  <div className="w-9 h-9 rounded-full bg-primary/8 text-primary grid place-items-center shrink-0 relative">
                    <ShoppingCart className="w-4 h-4" />
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-card grid place-items-center">
                      <StatusDot status={o.status} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] truncate">{o.order_number}</div>
                    <div className="text-[10.5px] text-muted-foreground truncate">{fmtDate(o.created_at)} · <span className="capitalize">{(o.status || "placed").replace(/_/g, " ")}</span></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-[13px] tabular-nums">{money(o.pricing?.total)}</div>
                    <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground font-semibold">{o.channel || "web"}</div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </PanelCard>

        <PanelCard
          icon={PackageX}
          title="Low stock alerts"
          subtitle="Reorder priorities"
          tone="amber"
          testId="panel-low-stock"
          cta={<Link to={`${base}/inventory`} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid="link-inventory">Inventory <ArrowRight className="w-3 h-3" /></Link>}
        >
          <CardContent className="p-0">
            <div className="divide-y divide-border/60">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4"><Skeleton className="h-8 w-full" /></div>
              ))}
              {!loading && lowStockItems.length === 0 && (
                <div className="p-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-3">
                    <Package className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-sm font-semibold">All stocked up</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Every SKU above reorder threshold.</div>
                </div>
              )}
              {lowStockItems.map((it) => {
                const onHand = it.qty_on_hand ?? 0;
                const critical = onHand === 0;
                return (
                  <button
                    key={it.id || it.sku || it.product_id}
                    type="button"
                    onClick={() => nav(`${base}/inventory`)}
                    className="w-full text-left flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors"
                    data-testid={`low-stock-row-${it.sku || it.product_id}`}
                  >
                    <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${critical ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600"}`}>
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[13px] truncate">{it.product_name || it.sku || it.product_id}</div>
                      <div className="text-[10.5px] text-muted-foreground truncate">
                        Reorder at <span className="font-semibold text-foreground/80">{it.reorder_level ?? 0}</span> · SKU {it.sku || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-bold text-[13px] tabular-nums ${critical ? "text-rose-600" : "text-amber-600"}`}>{onHand}</div>
                      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground font-semibold">on hand</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </PanelCard>
      </section>

      {/* ─── Phase 2 — Top selling products + Top categories ──────── */}
      <section className="grid lg:grid-cols-2 gap-4 md:gap-5">
        <PanelCard
          icon={Package}
          title="Top selling products"
          subtitle="Fastest movers"
          tone="amber"
          testId="panel-top-products"
          cta={<Link to={`${base}/products`} className="text-[11px] font-semibold text-primary hover:underline">All →</Link>}
        >
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4"><Skeleton className="h-32 w-full" /></div>
            ) : (() => {
              const prodAgg = {};
              allOrders.forEach((o) => {
                if (["cancelled", "refunded"].includes(o.status)) return;
                (o.items || []).forEach((it) => {
                  const key = it.product_id || it.name || it.sku;
                  if (!key) return;
                  if (!prodAgg[key]) prodAgg[key] = { id: it.product_id, name: it.name || it.product_name || key, qty: 0, revenue: 0, image: it.image };
                  prodAgg[key].qty += Number(it.qty || it.quantity || 1);
                  prodAgg[key].revenue += Number(it.total || it.line_total || (it.price || 0) * (it.qty || 1));
                });
              });
              let rows = Object.values(prodAgg).sort((a, b) => b.qty - a.qty).slice(0, 5);
              if (rows.length === 0) {
                rows = allProducts.slice(0, 5).map((pr) => ({
                  id: pr.id, name: pr.name, qty: 0,
                  revenue: Number(pr.mrp || pr.price || 0),
                  image: pr.image_url || (pr.images || [])[0],
                }));
              }
              if (rows.length === 0) return <div className="p-8 text-center text-xs text-muted-foreground">No products yet</div>;
              const max = rows[0]?.qty || rows[0]?.revenue || 1;
              return (
                <div className="divide-y divide-border/60">
                  {rows.map((pr, i) => {
                    const metric = pr.qty > 0 ? pr.qty : pr.revenue;
                    const pct = Math.max(6, Math.round((metric / max) * 100));
                    return (
                      <button
                        key={pr.id || i}
                        type="button"
                        onClick={() => pr.id && nav(`${base}/products`)}
                        className="w-full text-left px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors"
                        data-testid={`store-top-product-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-md bg-secondary text-foreground/80 grid place-items-center shrink-0 text-[11px] font-bold">{i + 1}</div>
                          <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-600 grid place-items-center shrink-0 overflow-hidden">
                            {pr.image ? <img src={pr.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[13px] truncate">{pr.name}</div>
                            <div className="text-[10.5px] text-muted-foreground truncate">{pr.qty > 0 ? `${pr.qty} sold · ${money(pr.revenue)}` : "In catalog"}</div>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 opacity-40" />
                        </div>
                        <div className="mt-2 ml-9 h-1 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-amber-500 to-orange-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </PanelCard>

        <PanelCard
          icon={FolderTree}
          title="Top categories"
          subtitle="By product count"
          tone="indigo"
          testId="panel-top-categories"
          cta={<Link to={`${base}/categories`} className="text-[11px] font-semibold text-primary hover:underline">All →</Link>}
        >
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4"><Skeleton className="h-32 w-full" /></div>
            ) : (() => {
              const catCounts = {};
              allProducts.forEach((pr) => {
                const cid = pr.category_id || pr.category || "uncat";
                catCounts[cid] = (catCounts[cid] || 0) + 1;
              });
              const catById = {};
              categories.forEach((c) => { catById[c.id] = c; });
              const rows = Object.entries(catCounts)
                .map(([cid, count]) => ({ id: cid, name: catById[cid]?.name || (cid === "uncat" ? "Uncategorised" : cid.slice(0, 8)), count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
              const max = rows[0]?.count || 1;
              if (rows.length === 0) return <div className="p-8 text-center text-xs text-muted-foreground">No categories yet</div>;
              return (
                <div className="divide-y divide-border/60">
                  {rows.map((c, i) => {
                    const pct = Math.round((c.count / max) * 100);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => c.id && c.id !== "uncat" && nav(`${base}/products?category=${encodeURIComponent(c.id)}`)}
                        className="w-full text-left px-4 md:px-5 py-3 hover:bg-secondary/40 transition-colors"
                        data-testid={`store-top-category-${c.name}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-6 h-6 rounded-md bg-secondary text-foreground/80 grid place-items-center shrink-0 text-[11px] font-bold">{i + 1}</div>
                          <div className="flex-1 min-w-0 font-semibold text-[13px] truncate">{c.name}</div>
                          <div className="font-bold text-[12.5px] tabular-nums shrink-0">{c.count}</div>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 opacity-40" />
                        </div>
                        <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </PanelCard>
      </section>
    </div>
  );
}
